/**
 * A1-93 BLOCKER 2 — what "Supersede this filing" does when the form no
 * longer matches the locked calc.
 *
 * A locked filing is refiled only through a supersede, which re-runs the
 * SAME files, period and client. It used to re-run the OLD calc's rates
 * too, and opening the replacement then wrote those back over the
 * operator's corrected rate — the new filing carried the old rate and the
 * edit vanished. Now:
 *   - exchange rates / country filter changed -> the supersede carries the
 *     form's values (SupersedeRequest accepts them, backend A1-93);
 *   - files, period or client changed -> a supersede cannot honour that,
 *     so it is refused with the reason (Start over makes a new calc).
 */
import { monthlyFxInvalidCells, formatFxInvalidMessage } from "../components/calculate/flaggedFx";
import { canonicalPeriodFx, canonicalMonthlyFx } from "./calcInputs";

const CARRIABLE = new Set(["fx", "country_filter"]);

// Shown for Step 6 "Supersede this filing" AND for the Inspect actions that
// supersede an unlocked calc, so it names neither (A1-93 round 4 wording).
export const SUPERSEDE_BLOCKED_TEXT =
  "A replacement calculation keeps this calculation's files, period and client. Put those back on Run calculation, or use Start over for a new calculation.";

const positive = (v) => {
  const n = Number(String(v ?? "").trim());
  return String(v ?? "").trim() !== "" && Number.isFinite(n) && n > 0;
};

// Blank grid cells are not rates; the backend rejects them, so drop them.
export const cleanPeriodFx = (raw) => {
  const out = {};
  for (const [tgt, srcs] of Object.entries(raw || {})) {
    if (!srcs || typeof srcs !== "object") continue;
    const inner = Object.fromEntries(Object.entries(srcs).filter(([, r]) => positive(r)));
    if (Object.keys(inner).length) out[tgt] = inner;
  }
  return out;
};

export const cleanMonthlyFx = (raw) => {
  const out = {};
  for (const [src, tgts] of Object.entries(raw || {})) {
    for (const [tgt, months] of Object.entries(tgts || {})) {
      const kept = Object.fromEntries(Object.entries(months || {}).filter(([, e]) => positive(e?.rate)));
      if (Object.keys(kept).length) out[src] = { ...(out[src] || {}), [tgt]: kept };
    }
  }
  return out;
};

// Review round 2 (R2-1) — "1,16" is not a blank: dropping it sent NO rate
// and replaced a filed return with one whose rows had no rate. A typed
// value that is not a number above zero refuses the supersede, naming the
// cell (the same rule a first run applies: flaggedFx.monthlyFxInvalidCells).
export const invalidFxCells = (periodFxRates, monthlyFxRates) => {
  const out = [];
  for (const [tgt, srcs] of Object.entries(periodFxRates || {})) {
    for (const [src, r] of Object.entries(srcs && typeof srcs === "object" ? srcs : {})) {
      if (String(r ?? "").trim() !== "" && !positive(r)) out.push({ src, tgt, month: "period" });
    }
  }
  out.push(...monthlyFxInvalidCells(monthlyFxRates));
  return out;
};

// A rate the locked filing used that is now EMPTY on the form would be
// replaced by no rate at all — refused too, naming it.
const emptiedRates = (calc, periodFxRates, monthlyFxRates) => {
  const was = { ...canonicalPeriodFx(calc?.period_fx_rates, calc?.regime), ...canonicalMonthlyFx(calc?.monthly_fx_rates) };
  const now = { ...canonicalPeriodFx(periodFxRates, calc?.regime), ...canonicalMonthlyFx(monthlyFxRates) };
  return Object.keys(was).filter((k) => !(k in now)).map((k) => k.replace("<", " ← ").replace(">", " → "));
};

const refuse = (blocked) => ({ blocked, overrides: {}, carries: false });

/** { blocked: string|null, overrides: object, carries: bool } */
export const supersedePlan = (state, { calc, periodFxRates, monthlyFxRates, countryFilter } = {}) => {
  const changed = state?.derived?.staleInputs || [];
  if (!changed.length) return { blocked: null, overrides: {}, carries: false };
  if (changed.some((k) => !CARRIABLE.has(k))) return refuse(SUPERSEDE_BLOCKED_TEXT);
  const invalid = invalidFxCells(periodFxRates, monthlyFxRates);
  if (invalid.length) return refuse(formatFxInvalidMessage(invalid));
  const emptied = calc ? emptiedRates(calc, periodFxRates, monthlyFxRates) : [];
  if (emptied.length) return refuse(`The exchange rate the filing used for ${emptied.join(", ")} is empty on the form. Enter it before replacing the filing.`);
  return {
    blocked: null,
    carries: true,
    overrides: {
      period_fx_rates: cleanPeriodFx(periodFxRates),
      monthly_fx_rates: cleanMonthlyFx(monthlyFxRates),
      country_filter: String(countryFilter || "ALL").toUpperCase(),
    },
  };
};
